import {useRef, useSyncExternalStore} from "react";
import type {Indexes} from "tinybase/with-schemas";
import {equalIds, getOrCreate, RowIdsSnapshot} from "./row-ids-snapshot";

const sliceCache = new WeakMap<Indexes<any>, Map<string, RowIdsSnapshot<any>>>();
const getSlice = <T>(indexes: Indexes<any>, indexName: string, key: string) =>
  getOrCreate(
    sliceCache,
    indexes,
    `${indexName}=${key}`,
    () =>
      new RowIdsSnapshot<T>(
        () => indexes.getSliceRowIds(indexName, key),
        (onChange) => {
          const listenerId = indexes.addSliceRowIdsListener(indexName, key, onChange);
          return () => {
            indexes.delListener(listenerId);
          };
        }
      )
  ) as RowIdsSnapshot<T>;

export const useIndex = <T>(key: string, indexes: Indexes<any>, indexName: string) => {
  const slice = getSlice<T>(indexes, indexName, key);
  return useSyncExternalStore(slice.subscribe, slice.getSnapshot);
};

type Subscribe = (onChange: () => void) => () => void;

// one stable subscribe function per index, so useSyncExternalStore doesn't resubscribe on every
// render
const allSlicesCache = new WeakMap<Indexes<any>, Map<string, Subscribe>>();
const getAllSlicesSubscribe = (indexes: Indexes<any>, indexName: string) =>
  getOrCreate(allSlicesCache, indexes, indexName, (): Subscribe => (onChange) => {
    const listenerId = indexes.addSliceRowIdsListener(indexName, null, onChange);
    return () => {
      indexes.delListener(listenerId);
    };
  });

const getSlices = <TKey extends string, TValue>(
  keys: Set<TKey>,
  indexes: Indexes<any>,
  indexName: string
) =>
  new Map<TKey, TValue[]>(
    [...keys].map((key) => [key, indexes.getSliceRowIds(indexName, key) as TValue[]])
  );

const equalSlices = <TKey, TValue>(a: Map<TKey, TValue[]>, b: Map<TKey, TValue[]>) =>
  a.size === b.size &&
  [...b].every(([key, ids]) => {
    const prevIds = a.get(key);
    return prevIds !== undefined && equalIds(prevIds, ids);
  });

// The keys differ per caller, so unlike `useIndex` there's no snapshot to share: each caller keeps
// its own and reuses it while the slices match, as useSyncExternalStore needs a stable snapshot.
export const useMultiKeyIndex = <TKey extends string, TValue>(
  keys: Set<TKey>,
  indexes: Indexes<any>,
  indexName: string
): Map<TKey, TValue[]> => {
  const snapshotRef = useRef<Map<TKey, TValue[]> | null>(null);
  const getSnapshot = () => {
    const next = getSlices<TKey, TValue>(keys, indexes, indexName);
    const prev = snapshotRef.current;
    if (prev && equalSlices(prev, next)) return prev;
    snapshotRef.current = next;
    return next;
  };
  return useSyncExternalStore(getAllSlicesSubscribe(indexes, indexName), getSnapshot);
};
